/**
 * Modules the shell provides itself: the Home dashboard, plus a placeholder
 * for every main-nav module that later waves build (Schedule, Patients,
 * Charting, ...). A placeholder steps aside automatically when a real
 * module with the same id, or one claiming the same route path, appears in
 * `src/modules/` (no edit here needed).
 *
 * Placeholders are honest: their page says what is coming and when, never
 * pretends to work.
 */
import { defineManifest, type ModuleManifest, type ModuleManifestInput } from '@velora/contracts';
import { defineWebModule, type WebModule } from '../module.js';

export interface PlaceholderInfo {
  /** Short outcome sentence for the empty state. */
  pitch: string;
  /** What arrives, in the user's words. */
  bullets: readonly string[];
  /** Build wave (BUILD-PLAN 5). */
  wave: number;
}

type Spec = Omit<ModuleManifestInput, 'owner' | 'version' | 'entitlementKey'> & {
  entitlementKey?: string | null;
  info: PlaceholderInfo;
};

const PLACEHOLDERS: readonly Spec[] = [
  {
    id: 'schedule',
    title: 'Schedule',
    routes: [{ id: 'day', path: '/schedule', title: 'Schedule', officeScope: 'location' }],
    nav: [
      {
        label: 'Schedule',
        labelKey: 'schedule',
        group: 'schedule',
        routeId: 'day',
        icon: 'calendar-days',
        order: 10,
      },
    ],
    commands: [
      {
        id: 'schedule.open',
        title: 'Go to Schedule',
        keywords: ['calendar', 'appointments', 'day'],
        routeId: 'day',
      },
    ],
    shortcuts: [{ keys: 'g s', description: 'Go to Schedule', routeId: 'day' }],
    info: {
      pitch:
        'Run the day from one live grid: drag to book and move, with every change written back to your PMS.',
      bullets: [
        'Operatory and provider columns with live updates from every workstation',
        'Drag to create, move and resize with conflict warnings and undo',
        'Docked appointment panel, flow board, pinboard and find-openings',
      ],
      wave: 1,
    },
  },
  {
    id: 'patients',
    title: 'Patients',
    routes: [{ id: 'list', path: '/patients', title: 'Patients', officeScope: 'practice' }],
    nav: [
      {
        label: 'Patients',
        labelKey: 'familyFile',
        group: 'patients',
        routeId: 'list',
        icon: 'users-round',
        order: 10,
      },
    ],
    commands: [
      {
        id: 'patients.open',
        title: 'Go to Patients',
        keywords: ['family', 'household', 'records'],
        routeId: 'list',
      },
    ],
    shortcuts: [{ keys: 'g p', description: 'Go to Patients', routeId: 'list' }],
    info: {
      pitch:
        'Every patient and household shared across your offices, with alerts, balances and history at a glance.',
      bullets: [
        'Overview tiles, demographics, household and contact preferences',
        'Medical alerts and premedication flags that follow the patient',
        'Timeline of visits, messages, calls and payments',
      ],
      wave: 1,
    },
  },
  {
    id: 'inbox',
    title: 'Messages',
    routes: [
      {
        id: 'threads',
        path: '/communications',
        title: 'Messages',
        officeScope: 'location-or-all',
      },
    ],
    nav: [
      {
        label: 'Messages',
        group: 'patients',
        routeId: 'threads',
        icon: 'message-square',
        order: 20,
      },
    ],
    commands: [
      {
        id: 'inbox.open',
        title: 'Go to Messages',
        keywords: ['inbox', 'text', 'sms', 'messages', 'communications'],
        routeId: 'threads',
      },
    ],
    shortcuts: [{ keys: 'g i', description: 'Go to Messages', routeId: 'threads' }],
    info: {
      pitch: 'Texts, calls and confirmations for every office in one shared inbox.',
      bullets: [
        'Two-way texting with templates and consent handling',
        'Confirmations that update the appointment automatically',
        'Missed-call text-back and call history per patient',
      ],
      wave: 1,
    },
  },
  {
    id: 'chart',
    title: 'Charting',
    routes: [{ id: 'chart', path: '/charting', title: 'Charting', officeScope: 'location' }],
    nav: [
      {
        label: 'Charting',
        labelKey: 'chart',
        group: 'clinical',
        routeId: 'chart',
        icon: 'smile',
        order: 10,
      },
    ],
    commands: [
      {
        id: 'chart.open',
        title: 'Go to Charting',
        keywords: ['tooth', 'teeth', 'procedures', 'perio'],
        routeId: 'chart',
      },
    ],
    shortcuts: [{ keys: 'g c', description: 'Go to Charting', routeId: 'chart' }],
    info: {
      pitch:
        'A fast tooth chart drawn from procedure records, with perio and treatment plans beside it.',
      bullets: [
        'Click or keyboard charting with Dentrix, Eaglesoft or Open Dental colours',
        'Keyboard perio exams with bleeding and suppuration',
        'Treatment plans with insurance estimates that explain themselves',
      ],
      wave: 2,
    },
  },
  {
    id: 'imaging',
    title: 'Imaging',
    routes: [{ id: 'viewer', path: '/imaging', title: 'Imaging', officeScope: 'location' }],
    nav: [
      {
        label: 'Imaging',
        labelKey: 'imaging',
        group: 'clinical',
        routeId: 'viewer',
        icon: 'scan-line',
        order: 20,
      },
    ],
    commands: [
      {
        id: 'imaging.open',
        title: 'Go to Imaging',
        keywords: ['xray', 'radiograph', 'bwx', 'pano', 'fmx'],
        routeId: 'viewer',
      },
    ],
    shortcuts: [{ keys: 'g x', description: 'Go to Imaging', routeId: 'viewer' }],
    info: {
      pitch: 'Every radiograph and photo in one viewer, mounted and linked to the chart.',
      bullets: [
        'FMX, bitewing and pano mounts with measurement tools',
        'Read existing DEXIS studies from the office',
        'Findings that become chart conditions with one click',
      ],
      wave: 2,
    },
  },
  {
    id: 'billing',
    title: 'Billing',
    routes: [{ id: 'ledger', path: '/billing', title: 'Billing', officeScope: 'location-or-all' }],
    nav: [
      {
        label: 'Billing',
        labelKey: 'ledger',
        group: 'revenue',
        routeId: 'ledger',
        icon: 'wallet',
        order: 10,
      },
    ],
    commands: [
      {
        id: 'billing.open',
        title: 'Go to Billing',
        keywords: ['ledger', 'balance', 'payments', 'claims', 'insurance'],
        routeId: 'ledger',
      },
    ],
    shortcuts: [{ keys: 'g l', description: 'Go to Billing', routeId: 'ledger' }],
    info: {
      pitch: 'Ledgers, claims and payments that reconcile on their own.',
      bullets: [
        'Family ledger and aging, read-only from your PMS first',
        'Claims, ERA auto-posting and eligibility checks',
        'Text-to-pay, payment plans and statements',
      ],
      wave: 2,
    },
  },
  {
    id: 'analytics',
    title: 'Analytics',
    routes: [
      { id: 'boards', path: '/analytics', title: 'Analytics', officeScope: 'location-or-all' },
    ],
    nav: [
      { label: 'Analytics', group: 'analytics', routeId: 'boards', icon: 'bar-chart-3', order: 10 },
    ],
    commands: [
      {
        id: 'analytics.open',
        title: 'Go to Analytics',
        keywords: ['reports', 'kpi', 'huddle', 'metrics'],
        routeId: 'boards',
      },
    ],
    shortcuts: [{ keys: 'g a', description: 'Go to Analytics', routeId: 'boards' }],
    info: {
      pitch: 'Every number explained and clickable, per office and for the whole practice.',
      bullets: [
        'Morning huddle with yesterday, today and tomorrow',
        'Production, collections and provider boards by office',
        'Metric dictionary: every tile opens its formula and the rows behind it',
      ],
      wave: 3,
    },
  },
  {
    id: 'assistant',
    title: 'AI Assistant',
    entitlementKey: 'ai_assistant',
    routes: [
      { id: 'ask', path: '/assistant', title: 'AI Assistant', officeScope: 'location-or-all' },
    ],
    nav: [
      { label: 'AI Assistant', group: 'analytics', routeId: 'ask', icon: 'sparkles', order: 20 },
    ],
    commands: [
      {
        id: 'assistant.open',
        title: 'Go to AI Assistant',
        keywords: ['ask', 'ai', 'scribe'],
        routeId: 'ask',
      },
    ],
    info: {
      pitch:
        'Ask questions about your practice in plain words and get the answer, the query and the rows.',
      bullets: [
        'Answers that always show their working',
        'Turn an answer into an action, with your confirmation',
        'Clinical note drafts from the chairside conversation',
      ],
      wave: 3,
    },
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    routes: [{ id: 'store', path: '/marketplace', title: 'Marketplace', officeScope: 'practice' }],
    // No side-nav item: the user menu and Settings open the Store.
    nav: [],
    commands: [
      {
        id: 'marketplace.open',
        title: 'Go to Marketplace',
        keywords: ['add-ons', 'modules', 'store', 'trial'],
        routeId: 'store',
      },
    ],
    shortcuts: [{ keys: 'g m', description: 'Go to Marketplace', routeId: 'store' }],
    info: {
      pitch:
        'Turn on add-ons per office, try any of them free for 14 days, and see one bill per office.',
      bullets: [
        'Add-ons grouped by outcome: fill the schedule, get paid, answer every call',
        'Per-office trials and billing through Square',
        'Clear data-access notes for every add-on',
      ],
      wave: 4,
    },
  },
  {
    id: 'settings',
    title: 'Settings',
    routes: [{ id: 'home', path: '/settings', title: 'Settings', officeScope: 'practice' }],
    nav: [{ label: 'Settings', group: 'settings', routeId: 'home', icon: 'settings', order: 10 }],
    commands: [
      {
        id: 'settings.open',
        title: 'Go to Settings',
        keywords: ['preferences', 'offices', 'users', 'roles'],
        routeId: 'home',
      },
    ],
    shortcuts: [{ keys: 'g ,', description: 'Go to Settings', routeId: 'home' }],
    info: {
      pitch: 'Offices, people, roles and how each office connects to its PMS.',
      bullets: [
        'Add offices and set their hours, operatories and providers',
        'Users, roles and which offices each person can see',
        'Connector health and PMS write-back settings per office',
      ],
      wave: 1,
    },
  },
];

const placeholderInfo = new Map<string, PlaceholderInfo>();
const placeholderManifests = new Map<string, ModuleManifest>();

export const PLACEHOLDER_MODULES: readonly WebModule[] = PLACEHOLDERS.map((spec) => {
  const { info, entitlementKey = null, ...rest } = spec;
  const manifest = defineManifest({
    ...rest,
    version: '0.0.0',
    owner: 'WP0-SHELL',
    entitlementKey,
  });
  placeholderInfo.set(manifest.id, info);
  placeholderManifests.set(manifest.id, manifest);
  const pages = Object.fromEntries(
    manifest.routes.map((r) => [
      r.id,
      () =>
        import('./PlaceholderPage.js').then((m) => ({ default: m.placeholderPage(manifest.id) })),
    ]),
  );
  return defineWebModule({ manifest, pages });
});

export function getPlaceholder(
  id: string,
): { manifest: ModuleManifest; info: PlaceholderInfo } | undefined {
  const manifest = placeholderManifests.get(id);
  const info = placeholderInfo.get(id);
  return manifest && info ? { manifest, info } : undefined;
}

export const HOME_MODULE: WebModule = defineWebModule({
  manifest: defineManifest({
    id: 'home',
    title: 'Home',
    version: '0.1.0',
    owner: 'WP0-SHELL',
    entitlementKey: null,
    permissions: ['money.view'],
    routes: [{ id: 'dashboard', path: '/home', title: 'Home', officeScope: 'location-or-all' }],
    nav: [
      {
        label: 'Home',
        group: 'dashboard',
        routeId: 'dashboard',
        icon: 'layout-dashboard',
        order: 0,
      },
    ],
    commands: [
      {
        id: 'home.open',
        title: 'Go to Home',
        keywords: ['dashboard', 'huddle', 'kpi', 'today'],
        routeId: 'dashboard',
      },
    ],
    shortcuts: [{ keys: 'g h', description: 'Go to Home', routeId: 'dashboard' }],
    sseEntityKeys: [],
  }),
  pages: { dashboard: () => import('../home/HomePage.js') },
});

/**
 * Discovered modules plus the shell's own. A placeholder is dropped when a
 * discovered module has its id or any of its route paths, so the first real
 * Schedule module replaces the placeholder without touching this file.
 */
export function withBuiltins(discovered: readonly WebModule[]): WebModule[] {
  const ids = new Set(discovered.map((m) => m.manifest.id));
  const paths = new Set(discovered.flatMap((m) => m.manifest.routes.map((r) => r.path)));
  const shortcuts = new Set(
    discovered.flatMap((m) =>
      m.manifest.shortcuts
        .filter((s) => s.scope === 'global')
        .map((s) => s.keys.trim().toLowerCase()),
    ),
  );
  const keep = (m: WebModule) =>
    !ids.has(m.manifest.id) && !m.manifest.routes.some((r) => paths.has(r.path));
  const builtins = [HOME_MODULE, ...PLACEHOLDER_MODULES].filter(keep).map((m) =>
    // A real module may bind the G-sequence a placeholder used.
    m.manifest.shortcuts.some((s) => shortcuts.has(s.keys.trim().toLowerCase()))
      ? {
          ...m,
          manifest: {
            ...m.manifest,
            shortcuts: m.manifest.shortcuts.filter(
              (s) => !shortcuts.has(s.keys.trim().toLowerCase()),
            ),
          },
        }
      : m,
  );
  return [...discovered, ...builtins];
}

export function isPlaceholder(m: WebModule): boolean {
  return m.manifest.owner === 'WP0-SHELL' && placeholderManifests.has(m.manifest.id);
}

/** What a locked Marketplace add-on page says (pitch and bullets). */
export type LockedInfo = Pick<PlaceholderInfo, 'pitch' | 'bullets'>;

/**
 * Pitches for real add-on modules, shown on the locked panel to offices that
 * do not hold the add-on. Placeholders carry their own `info`.
 */
const ADDON_INFO: ReadonlyMap<string, LockedInfo> = new Map([
  [
    'lightboard',
    {
      pitch:
        'Every operatory at a glance. One tap calls the doctor, and the room shows who is on the way.',
      bullets: [
        'Ready for Dr with a doctor queue',
        'Office pages: doorbell, urgent, emergency',
        'Works through a WAN outage and catches up',
        'Wall mode for a TV or tablet',
      ],
    },
  ],
  [
    'membership',
    {
      pitch:
        'Sell in-house membership plans to uninsured patients: dues collected by Square, included cleanings and exams tracked, member pricing posted automatically.',
      bullets: [
        'Plan builder with ready-made templates',
        'Monthly or annual dues on the card on file',
        'Declines worklist with automatic retries',
        'Member pricing posted to the ledger',
      ],
    },
  ],
]);

/** Locked-panel copy for a module: its placeholder info, else its add-on pitch. */
export function getLockedInfo(id: string): LockedInfo | undefined {
  return placeholderInfo.get(id) ?? ADDON_INFO.get(id);
}
